import type { WorldAnnotation } from '../types/worldAnnotation';

export const sunnySideAnnotations: WorldAnnotation[] = [
  {
    id: 'sunny-village-square',
    name: 'Village Square',
    description: 'The social heart of the village where conversations and chance meetings gather.',
    category: 'area',
    shape: 'rect',
    position: { x: 6, y: 19 },
    size: { width: 11, height: 10 },
    function: 'social',
    capacity: 10,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-village-signpost',
    name: 'Village Signpost',
    description: 'A weathered sign marking the village square entrance.',
    category: 'sign',
    shape: 'point',
    position: { x: 10, y: 26 },
    playerVisible: true,
    examineText: 'Village Square: talk to locals and share stories.',
    interactionRadius: 2,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-central-fountain',
    name: 'Central Fountain',
    description: 'A fountain that anchors the plaza and gives people somewhere obvious to gather.',
    category: 'landmark',
    shape: 'rect',
    position: { x: 13, y: 20 },
    size: { width: 4, height: 4 },
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-market-stalls',
    name: 'Market Stalls',
    description: 'A row of informal stalls used for barter, gossip, and quick exchanges.',
    category: 'furniture',
    shape: 'rect',
    position: { x: 5, y: 14 },
    size: { width: 8, height: 3 },
    function: 'social',
    capacity: 4,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-forest-gate',
    name: 'Forest Gate',
    description: 'The threshold where the open village path narrows into the forest trail.',
    category: 'door',
    shape: 'point',
    position: { x: 33, y: 16 },
    playerVisible: true,
    examineText: 'A path gate marking the start of the forest trail.',
    interactionRadius: 2,
    doorTarget: { tileX: 36, tileY: 16, zone: 'forest' },
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-village-return-gate',
    name: 'Village Return Gate',
    description: 'The way back from the forest into the brighter village path.',
    category: 'door',
    shape: 'point',
    position: { x: 32, y: 16 },
    playerVisible: true,
    examineText: 'A path gate leading back toward the village square.',
    interactionRadius: 2,
    doorTarget: { tileX: 30, tileY: 16, zone: 'village' },
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-forest-signpost',
    name: 'Forest Signpost',
    description: 'A directional sign warning travelers to keep a lantern close.',
    category: 'sign',
    shape: 'point',
    position: { x: 46, y: 26 },
    playerVisible: true,
    examineText: 'Forest Path: keep your lantern close and your pace steady.',
    interactionRadius: 2,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-forest-path',
    name: 'Forest Path',
    description:
      'A winding path under dense trees where movement becomes quieter and more deliberate.',
    category: 'area',
    shape: 'rect',
    position: { x: 38, y: 8 },
    size: { width: 15, height: 18 },
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-lake-edge',
    name: 'Lake Edge',
    description: 'The calm edge of the water, suited for reflection and quiet conversations.',
    category: 'nature',
    shape: 'rect',
    position: { x: 44, y: 30 },
    size: { width: 12, height: 10 },
    function: 'rest',
    capacity: 4,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-bridge-crossing',
    name: 'Bridge Crossing',
    description: 'A narrow crossing that funnels movement between districts.',
    category: 'landmark',
    shape: 'rect',
    position: { x: 28, y: 28 },
    size: { width: 6, height: 3 },
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-library-door',
    name: 'Library Door',
    description: 'The doorway leading from the village path into the library district.',
    category: 'door',
    shape: 'point',
    position: { x: 28, y: 31 },
    playerVisible: true,
    examineText: 'A wooden doorway leading into the library district.',
    interactionRadius: 2,
    doorTarget: { tileX: 31, tileY: 36, zone: 'library' },
    interactions: [
      {
        action: 'open_door',
        label: 'Open Door',
        description: 'Lift the latch and swing the library door open.',
        effectType: 'state_change',
        effectPayload: 'The latch clicks free and the door eases inward.',
        stateChange: {
          libraryDoorOpen: true,
        },
      },
      {
        action: 'enter',
        label: 'Enter Library',
        description: 'Step through the open doorway into the library district.',
        effectType: 'navigate',
        effectPayload: { tileX: 31, tileY: 36, zone: 'library' },
        requiredState: {
          libraryDoorOpen: true,
        },
      },
    ],
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-library-return-door',
    name: 'Village Door',
    description: 'The doorway that returns visitors from the library to the village path.',
    category: 'door',
    shape: 'point',
    position: { x: 31, y: 33 },
    playerVisible: true,
    examineText: 'A wooden doorway leading back to the village.',
    interactionRadius: 2,
    doorTarget: { tileX: 28, tileY: 29, zone: 'village' },
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-library-foyer',
    name: 'Library Foyer',
    description: 'A quiet arrival space where visitors orient themselves before browsing.',
    category: 'area',
    shape: 'rect',
    position: { x: 25, y: 34 },
    size: { width: 12, height: 8 },
    function: 'social',
    capacity: 6,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-library-signpost',
    name: 'Library Signpost',
    description: 'A sign introducing the library district and its promise of answers.',
    category: 'sign',
    shape: 'point',
    position: { x: 31, y: 40 },
    playerVisible: true,
    examineText: 'Library District: answers live on shelves and in questions.',
    interactionRadius: 2,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-reading-stacks',
    name: 'Reading Stacks',
    description: 'Dense shelves where references and stories are stored close together.',
    category: 'container',
    shape: 'rect',
    position: { x: 22, y: 44 },
    size: { width: 18, height: 7 },
    function: 'storage',
    capacity: 3,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-reading-table',
    name: 'Reading Table',
    description: 'A long shared table for reading, note taking, and quiet study.',
    category: 'furniture',
    shape: 'rect',
    position: { x: 28, y: 47 },
    size: { width: 6, height: 3 },
    function: 'research',
    capacity: 4,
    playerVisible: true,
    interactionRadius: 2,
    interactions: [
      {
        action: 'read_notes',
        label: 'Read Notes',
        description: 'Skim the loose notes and marked passages left on the table.',
        effectType: 'text',
        effectPayload:
          'Several notes cross-reference the same shelf numbers, with a margin reminder to follow the strongest thread first.',
      },
      {
        action: 'take_seat',
        label: 'Take Seat',
        description: 'Pull out a chair and settle in for a minute.',
        effectType: 'emote',
        effectPayload: {
          emote: 'thinking',
          duration: 3,
        },
      },
    ],
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'sunny-lantern-corner',
    name: 'Lantern Corner',
    description: 'A cozy corner lit warmly enough for one-on-one conversation.',
    category: 'landmark',
    shape: 'rect',
    position: { x: 40, y: 47 },
    size: { width: 5, height: 5 },
    function: 'rest',
    capacity: 2,
    createdBy: 'system',
    createdAt: 0,
  },
];

export const modernOfficeAnnotations: WorldAnnotation[] = [
  {
    id: 'office-reception',
    name: 'Reception Desk',
    description: 'The first stop for arrivals, check-ins, and quick directional questions.',
    category: 'furniture',
    shape: 'rect',
    position: { x: 27, y: 4 },
    size: { width: 8, height: 4 },
    function: 'work',
    capacity: 2,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-north-wing',
    name: 'North Wing',
    description: 'An upper-office zone of engineering pods, sprint boards, and focused work.',
    category: 'area',
    shape: 'rect',
    position: { x: 6, y: 3 },
    size: { width: 50, height: 15 },
    function: 'work',
    capacity: 8,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-north-wing-sign',
    name: 'North Wing Sign',
    description: 'A sign marking the engineering pod area.',
    category: 'sign',
    shape: 'point',
    position: { x: 31, y: 6 },
    playerVisible: true,
    examineText: 'North Wing: engineering pods and sprint boards.',
    interactionRadius: 2,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-sprint-board',
    name: 'Sprint Board',
    description: 'A planning board where active work and blockers are made visible.',
    category: 'device',
    shape: 'rect',
    position: { x: 21, y: 8 },
    size: { width: 5, height: 3 },
    function: 'display',
    capacity: 3,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-huddle-table',
    name: 'Huddle Table',
    description: 'A small table for quick tactical meetings and pair discussions.',
    category: 'furniture',
    shape: 'rect',
    position: { x: 37, y: 10 },
    size: { width: 4, height: 3 },
    function: 'meeting',
    capacity: 4,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-operations-hub',
    name: 'Operations Hub',
    description:
      'The busiest coordination zone for planning, monitoring, and shared decision making.',
    category: 'area',
    shape: 'rect',
    position: { x: 8, y: 24 },
    size: { width: 46, height: 11 },
    function: 'meeting',
    capacity: 10,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-operations-sign',
    name: 'Operations Sign',
    description: 'A sign labeling the central operations area.',
    category: 'sign',
    shape: 'point',
    position: { x: 31, y: 30 },
    playerVisible: true,
    examineText: 'Operations Hub: standups, planning, and coordination.',
    interactionRadius: 2,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-conference-table',
    name: 'Conference Table',
    description: 'A large shared table used for standups, planning, and review conversations.',
    category: 'furniture',
    shape: 'rect',
    position: { x: 27, y: 27 },
    size: { width: 8, height: 4 },
    function: 'meeting',
    capacity: 8,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-monitor-wall',
    name: 'Monitor Wall',
    description: 'A bank of shared displays showing team status and operational context.',
    category: 'device',
    shape: 'rect',
    position: { x: 12, y: 24 },
    size: { width: 6, height: 3 },
    function: 'display',
    capacity: 4,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-coffee-point',
    name: 'Coffee Point',
    description: 'An informal social spot where people pause, reset, and trade updates.',
    category: 'area',
    shape: 'rect',
    position: { x: 47, y: 24 },
    size: { width: 4, height: 4 },
    function: 'rest',
    capacity: 4,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-focus-pods',
    name: 'Focus Pods',
    description: 'A quieter zone of enclosed pods meant for concentrated solo work.',
    category: 'area',
    shape: 'rect',
    position: { x: 8, y: 39 },
    size: { width: 46, height: 9 },
    function: 'work',
    capacity: 6,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-pod-a',
    name: 'Pod A',
    description: 'A compact focus pod suited for deep work or private calls.',
    category: 'area',
    shape: 'rect',
    position: { x: 12, y: 41 },
    size: { width: 4, height: 4 },
    function: 'work',
    capacity: 1,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-pod-b',
    name: 'Pod B',
    description: 'A second focus pod on the east side of the room.',
    category: 'area',
    shape: 'rect',
    position: { x: 45, y: 41 },
    size: { width: 4, height: 4 },
    function: 'work',
    capacity: 1,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-support-desk-area',
    name: 'Support Desk',
    description: 'The incident and escalation area where urgent issues are triaged.',
    category: 'area',
    shape: 'rect',
    position: { x: 10, y: 53 },
    size: { width: 42, height: 7 },
    function: 'work',
    capacity: 6,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-support-sign',
    name: 'Support Sign',
    description: 'A sign identifying the support and escalation desk.',
    category: 'sign',
    shape: 'point',
    position: { x: 31, y: 56 },
    playerVisible: true,
    examineText: 'Support Desk: incidents, triage, and customer escalations.',
    interactionRadius: 2,
    createdBy: 'system',
    createdAt: 0,
  },
  {
    id: 'office-triage-board',
    name: 'Triage Board',
    description: 'A visible board for incidents, escalations, and response priorities.',
    category: 'device',
    shape: 'rect',
    position: { x: 24, y: 54 },
    size: { width: 7, height: 3 },
    function: 'display',
    capacity: 4,
    createdBy: 'system',
    createdAt: 0,
  },
];
